import React, { useState, useEffect } from 'react';
import { getMachineImagePath } from '../utils/machineImageUtils';
import { styles } from '../styles';

interface MachineIconProps {
  machineId?: string;
  machineType?: string;
  size?: number;
  className?: string;
}

export const MachineIcon: React.FC<MachineIconProps> = ({ 
  machineId, 
  machineType, 
  size = 20,
  className = "" 
}) => {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [imageError, setImageError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadImage = async () => {
      setLoading(true);
      setImageError(false);
      setImageSrc('');
      
      try {
        // Use machineType (bautyp) to get the machine image
        const typeToUse = machineType || machineId || '';
        const imagePath = await getMachineImagePath(typeToUse);
        if (imagePath) {
          setImageSrc(imagePath);
        } else {
          setImageError(true);
        }
      } catch (error) {
        setImageError(true);
      } finally {
        setLoading(false);
      }
    };

    loadImage();
  }, [machineId, machineType]);

  if (loading) {
    return (
      <div 
        className={`${styles.machineIcon.container} ${styles.machineIcon.default} ${className}`}
        style={{ width: size, height: size }}
      >
        <div className="animate-spin rounded-full border border-gray-400 border-t-transparent" 
             style={{ width: size * 0.6, height: size * 0.6 }} />
      </div>
    );
  }

  if (imageError || !imageSrc) {
    // Fallback to a simple machine emoji or icon
    return (
      <div 
        className={`${styles.machineIcon.container} ${styles.machineIcon.error} ${className}`}
        style={{ width: size, height: size }}
      >
        <span style={{ fontSize: size * 0.8 }}>🔧</span>
      </div>
    );
  }

  return (
    <div 
      className={`${styles.machineIcon.container} ${styles.machineIcon.success} ${className}`}
      style={{ width: size, height: size }}
    >
      <img 
        src={imageSrc}
        alt={`${machineId || 'Machine'}`}
        className="object-contain rounded-sm"
        style={{ width: size, height: size }}
        onError={() => setImageError(true)}
      />
    </div>
  );
};

export default MachineIcon;
